import { useCallback, useState } from 'react'

import type { ToastShowOptions } from '../../context/ToastContext'
import { toastForBrowserImageDownload } from '../../lib/clientImageDownload'
import {
  exportComicEpisodeAsPdf,
  exportComicEpisodeAsZip,
  exportComicPage,
} from '../../lib/comicPageExport'
import { getEpisodePage } from '../../lib/comicPages'
import { getActiveEpisode } from '../../lib/comicProjectMutations'
import type { ComicEpisode, ComicProject } from '../../lib/comicTypes'

type UseComicExportActionsOptions = {
  project: ComicProject | null
  episode: ComicEpisode | null
  showToast: (options: ToastShowOptions) => string
}

export function useComicExportActions({
  project,
  episode,
  showToast,
}: UseComicExportActionsOptions) {
  const [exportingPageId, setExportingPageId] = useState<string | null>(null)
  const [exportingEpisodeId, setExportingEpisodeId] = useState<string | null>(null)
  const [exportingEpisodePdfId, setExportingEpisodePdfId] = useState<string | null>(null)

  const handleExportPage = useCallback(
    async (pageId: string) => {
      if (!project || exportingPageId || exportingEpisodeId || exportingEpisodePdfId) return
      const ep =
        project.episodes.find((e) => e.pages.some((p) => p.id === pageId)) ??
        (episode ? episode : null)
      if (!ep) return
      const page = getEpisodePage(ep, pageId)
      if (!page) return
      setExportingPageId(pageId)
      try {
        const mode = await exportComicPage(ep, page, [project.title, ep.title])
        showToast(toastForBrowserImageDownload(mode))
      } catch (e) {
        showToast({
          message: e instanceof Error ? e.message : '导出失败',
          variant: 'error',
        })
      } finally {
        setExportingPageId(null)
      }
    },
    [project, episode, exportingPageId, exportingEpisodeId, exportingEpisodePdfId, showToast],
  )

  const handleExportEpisode = useCallback(
    async (episodeId: string) => {
      if (!project || exportingEpisodeId || exportingEpisodePdfId || exportingPageId) return
      const ep = getActiveEpisode(project, episodeId)
      setExportingEpisodeId(episodeId)
      try {
        const { exported, skipped } = await exportComicEpisodeAsZip(
          ep,
          [project.title, ep.title],
        )
        const suffix = skipped > 0 ? `，跳过 ${skipped} 页无图` : ''
        showToast({
          message: `已打包导出 ${exported} 页（ZIP）${suffix}`,
          variant: 'success',
          autoCloseMs: 4800,
        })
      } catch (e) {
        showToast({
          message: e instanceof Error ? e.message : '导出失败',
          variant: 'error',
        })
      } finally {
        setExportingEpisodeId(null)
      }
    },
    [project, exportingEpisodeId, exportingEpisodePdfId, exportingPageId, showToast],
  )

  const handleExportEpisodePdf = useCallback(
    async (episodeId: string) => {
      if (!project || exportingEpisodePdfId || exportingEpisodeId || exportingPageId) return
      const ep = getActiveEpisode(project, episodeId)
      setExportingEpisodePdfId(episodeId)
      try {
        const { exported, skipped } = await exportComicEpisodeAsPdf(
          ep,
          [project.title, ep.title],
        )
        const suffix = skipped > 0 ? `，跳过 ${skipped} 页无图` : ''
        showToast({
          message: `已导出 PDF（${exported} 页）${suffix}`,
          variant: 'success',
          autoCloseMs: 4800,
        })
      } catch (e) {
        showToast({
          message: e instanceof Error ? e.message : 'PDF 导出失败',
          variant: 'error',
        })
      } finally {
        setExportingEpisodePdfId(null)
      }
    },
    [project, exportingEpisodePdfId, exportingEpisodeId, exportingPageId, showToast],
  )

  return {
    exportingPageId,
    exportingEpisodeId,
    exportingEpisodePdfId,
    handleExportPage,
    handleExportEpisode,
    handleExportEpisodePdf,
  }
}
